import { useState, useEffect, useRef, useCallback } from 'react';
import dashboardService from '../../../api';

const useClientSummary = ({ widgetContents, clientData, allClients, widgetSettings, useCaseId, agentId, domainId, skipInitialFetch = false, isIBRole = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [fetchAttempts, setFetchAttempts] = useState(0);
  const [lastFetchedCustomerId, setLastFetchedCustomerId] = useState(null);
  const [lastContentHash, setLastContentHash] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [isLoadingCustomerData, setIsLoadingCustomerData] = useState(false);
  const [noClientsAvailable, setNoClientsAvailable] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const initialFetchDone = useRef(false);
  const loadingRef = useRef(false);
  const isMountedRef = useRef(true);
  const widgetContentRef = useRef(widgetContents);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    widgetContentRef.current = widgetContents;

    if (Object.keys(widgetContents).length > 0 && customerData && initialLoadComplete) {
      const timer = setTimeout(() => {
        fetchSummary();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [widgetContents]);

  useEffect(() => {
    if (!allClients || allClients.length === 0) {
      setNoClientsAvailable(true);
      setCustomerData(null);
      return;
    } else {
      setNoClientsAvailable(false);
    }

    if (clientData) {
      setCustomerData(clientData);

      if (lastFetchedCustomerId !== clientData.id) {
        setSummary(null);
        setLastFetchedCustomerId(clientData.id);
        setInitialLoadComplete(false);

        if (Object.keys(widgetContents).length > 0) {
          setTimeout(() => {
            fetchSummary(true);
          }, 300);
        }
      }
    }
  }, [clientData, allClients]);



  const generateContentHash = useCallback(() => {
    const contentString = JSON.stringify(widgetContentRef.current);
    let hash = 0;
    for (let i = 0; i < contentString.length; i++) {
      const char = contentString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }, []);

  const fetchSummary = useCallback(async (forceRefresh = false) => {

    if (loadingRef.current || !customerData?.id) {
      return;
    }

    if (initialFetchDone.current && !forceRefresh) {
      return;
    }

    const widgetContentCount = Object.keys(widgetContentRef.current || {}).length;

    if (widgetContentCount === 0) {
      return;
    }

    if (forceRefresh) {
      setFetchAttempts(0);
    } else if (fetchAttempts > 5) {
      return;
    }

    const currentContentHash = generateContentHash();

    if (!forceRefresh && customerData.id === lastFetchedCustomerId && currentContentHash === lastContentHash) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setFetchAttempts(prev => prev + 1);

    try {
      const widgetTexts = Object.entries(widgetContentRef.current || {}).map(([widgetId, content]) => {
        let formattedContent;

        if (typeof content === 'string') {
          formattedContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        } else if (typeof content === 'object') {
          try {
            formattedContent = JSON.stringify(content);
          } catch (e) {
            formattedContent = String(content);
          }
        } else {
          formattedContent = String(content || "");
        }

        return formattedContent;
      }).filter(content => content.length > 0);

      const formattedContent = widgetTexts.join('\n\n');

      if (!formattedContent.trim()) {
        if (isMountedRef.current) {
          setLoading(false);
          loadingRef.current = false;
        }
        return;
      }

      const result = await dashboardService.generateClientSummary(
        formattedContent,
        customerData.id,
        widgetSettings.summaryPrompt,
        useCaseId,
        clientData,
        isIBRole
      );


      if (isMountedRef.current) {
        setLoading(false);
        loadingRef.current = false;

        if (result.success) {
          const data = result.data;
          console.log('[ClientSummary] ✓ Summary received:', {
            customerId: customerData.id,
            shortSummary: data.shortSummary,
            longSummary: data.longSummary
          });
          setSummary({
            shortSummary: data.shortSummary,
            longSummary: data.longSummary
          });

          setLastFetchedCustomerId(customerData.id);
          setLastContentHash(currentContentHash);
          setError(null);
          setInitialLoadComplete(true);
        } else {
          console.error(`[ClientSummary] ✗ Failed:`, result.error);
          setError(result.error || "Failed to generate summary. Please try again.");
        }
      }
      initialFetchDone.current = true;
    } catch (err) {
      console.error("[ClientSummary] ✗ Exception:", err);
      if (isMountedRef.current) {
        setError("Failed to generate summary. Please try again.");
        setLoading(false);
        loadingRef.current = false;
      }
    }
  }, [customerData, generateContentHash, lastContentHash, lastFetchedCustomerId, fetchAttempts, widgetSettings.summaryPrompt]);

  useEffect(() => {
    if (!customerData || initialLoadComplete) return;

    const hasWidgetContents = Object.keys(widgetContents).length > 0;

    if (hasWidgetContents) {
      const timer = setTimeout(() => {
        fetchSummary(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [widgetContents, fetchSummary, customerData, initialLoadComplete]);

  useEffect(() => {
    if (skipInitialFetch) {
      return;
    }

    fetchSummary();
  }, [fetchSummary, skipInitialFetch]);

  return {
    loading,
    error,
    summary,
    noClientsAvailable,
    isLoadingCustomerData,
    customerData,
    fetchSummary
  };
};

export default useClientSummary;